import { SearchType } from '@aibees/elephant/types';
import {
  Label,
  LabelGrouping,
  LabelType,
  LabelVisibility,
} from '@aibees/elephant/types/label';
import { IconType } from '@aibees/shared/components';
import { listFilterLabelParser } from '../components/filter-tags/utils';
import {
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';
import { listAndLabelResponseFilterFn, listsResponseMapper } from '../utils';

export default function lablesFilterConfig(
  searchType: SearchType,
  employees?: boolean
): SearchFilterBase {
  const companiesLabels: SearchFilter = {
    name: 'Companies',
    type: SearchFilterType.NONE,
    index: 1,
    field: 'tags_account',
    key: 'tags_account',
    metadata: {
      tabs: {
        icon: 'business',
        iconColor: 'primary',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Companies Labels',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'labels_account_include',
        key: 'labels_account_include',
        icon: 'business',
        metadata: {
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Enter Company Labels, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'primary.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Shared'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.COMPANY,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.COMPANY,
                LabelVisibility.PRIVATE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.COMPANY,
                LabelVisibility.INTERNAL,
                userId
              ),
            valueKey: 'labels',
            label: 'Shared',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 1,
        layer: 4,
        field: 'labels_account_exclude',
        key: 'labels_account_exclude',
        icon: 'business',
        metadata: {
          color: 'error',
          icon: 'block',
          placeholder: 'Enter Company Labels, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'primary.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Shared'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.COMPANY,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.COMPANY,
                LabelVisibility.PRIVATE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.COMPANY,
                LabelVisibility.INTERNAL,
                userId
              ),
            valueKey: 'labels',
            label: 'Shared',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 2,
        layer: 4,
        field: 'companies_all_of_labels',
        key: 'companies_all_of_labels',
        icon: 'business',
        metadata: {
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Enter Company Labels, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'primary.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Shared'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.COMPANY,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.COMPANY,
                LabelVisibility.PRIVATE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.COMPANY,
                LabelVisibility.INTERNAL,
                userId
              ),
            valueKey: 'labels',
            label: 'Shared',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
    ],
  };
  const peopleLabels: SearchFilter = {
    type: SearchFilterType.NONE,
    icon: 'people',
    index: 2,
    field: 'tags_contact',
    key: 'tags_contact',
    name: 'People',
    metadata: {
      tabs: {
        icon: 'people',
        iconColor: 'success',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Contacts Labels',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'labels_contact_include',
        key: 'labels_contact_include',
        icon: 'people',
        metadata: {
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Enter People Labels, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'success.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Shared'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.PEOPLE,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.PEOPLE,
                LabelVisibility.PRIVATE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.PEOPLE,
                LabelVisibility.INTERNAL,
                userId
              ),
            valueKey: 'labels',
            label: 'Shared',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'labels_contact_exclude',
        key: 'labels_contact_exclude',
        icon: 'people',
        metadata: {
          color: 'error',
          icon: 'block',
          placeholder: 'Enter People Labels, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'success.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Shared'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.PEOPLE,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.PEOPLE,
                LabelVisibility.PRIVATE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.PEOPLE,
                LabelVisibility.INTERNAL,
                userId
              ),
            valueKey: 'labels',
            label: 'Shared',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'contacts_all_of_labels',
        key: 'contacts_all_of_labels',
        icon: 'people',
        metadata: {
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Enter People Labels, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'success.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Shared'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.PEOPLE,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.PEOPLE,
                LabelVisibility.PRIVATE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LABEL,
                LabelGrouping.PEOPLE,
                LabelVisibility.INTERNAL,
                userId
              ),
            valueKey: 'labels',
            label: 'Shared',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
    ],
  };

  return {
    name: 'In & Exclude Labels',
    icon: 'ic-label',
    index: 1,
    type: SearchFilterType.ROOT,
    category: [FilterCategory.RECORD_MANAGEMENT],
    pinned: true,
    items: employees
      ? [peopleLabels]
      : [
          {
            name: '',
            type: SearchFilterType.BUTTON_GROUP,
            icon: undefined,
            index: 0,
            key: '',
            field: '',
            metadata: {
              allowParallelItems: true,
              tabs: {
                defaultOpenIndex: searchType === SearchType.Company ? 0 : 1,
              },
            },
            config: { labelKey: 'label', valueKey: 'value' },
            items: [
              {
                name: 'Applied Filters',
                type: SearchFilterType.APPLIED_FILTERS,
                index: 0,
                icon: undefined,
                key: 'tags_applied_filters',
                field: 'tags_applied_filters',
                metadata: {},
              },
              ...[companiesLabels, peopleLabels],
            ],
          },
        ],
  };
}
